// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactElement, DragEvent, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Bookmark, ChevronRight, Pencil, SquareArrowOutUpRight } from 'lucide-react'

import styles from './SavedPage.module.css'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { fetchSavedNodes, updateSavedSessionTitle } from '../../../store/slices/savedNodesSlice'
import { updateSessionTitle } from '../../../store/slices/sessionSlice'
import { openTab, setActiveTab } from '../../../store/slices/sessionTabsSlice'
import { selectActiveSessionId } from '../../../store/selectors/sessionSelectors'
import { toggleNode } from '../../../store/slices/selectionSlice'
import { addItem as addCanvasItem } from '../../../store/slices/canvasSlice'
import { updateSession } from '../../../services/session'
import { setAiModelDragData, setAiModelDragging } from '../utils/drag'

export default function SavedPage(): ReactElement {
    const { t } = useTranslation('canvas')
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const { sessionGroups, loading, listLoaded } = useAppSelector(
        (s) => s.savedNodes
    )

    useEffect(() => {
        if (!listLoaded) {
            dispatch(fetchSavedNodes())
        }
    }, [dispatch, listLoaded])

    if (loading && sessionGroups.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.empty}>{t('common:loading')}</div>
            </div>
        )
    }

    if (sessionGroups.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.empty}>
                    <Bookmark className={styles.emptyIcon} />
                    {t('backstage.saveImagesHint')}
                </div>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {sessionGroups.map((group) => (
                <SessionSection
                    key={group.session_id}
                    sessionId={group.session_id}
                    title={group.session_title}
                    nodes={group.saved_nodes}
                    isActiveSession={group.session_id === activeSessionId}
                />
            ))}
        </div>
    )
}

interface SessionSectionProps {
    sessionId: string
    title: string | null
    nodes: { node_id: string; image_path: string }[]
    isActiveSession: boolean
}

function SessionSection({ sessionId, title, nodes, isActiveSession }: SessionSectionProps): ReactElement {
    const { t } = useTranslation('canvas')
    const dispatch = useAppDispatch()
    const [expanded, setExpanded] = useState(true)
    const [editing, setEditing] = useState(false)
    const [editValue, setEditValue] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    const displayTitle = title || `session ${sessionId.slice(0, 8)}`

    const handleEditStart = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        setEditValue(title || '')
        setEditing(true)
        setTimeout(() => inputRef.current?.focus(), 0)
    }, [title])

    const handleEditSave = useCallback(() => {
        setEditing(false)
        const trimmed = editValue.trim()
        if (trimmed && trimmed !== title) {
            dispatch(updateSessionTitle({ sessionId, title: trimmed }))
            dispatch(updateSavedSessionTitle({ sessionId, title: trimmed }))
            void updateSession(sessionId, { title: trimmed })
        }
    }, [editValue, title, sessionId, dispatch])

    const handleEditKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleEditSave()
        } else if (e.key === 'Escape') {
            setEditing(false)
        }
    }, [handleEditSave])

    const handleOpenSession = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        dispatch(openTab(sessionId))
        dispatch(setActiveTab(sessionId))
        try {
            window.history.pushState({}, '', `/session/${sessionId}`)
            window.dispatchEvent(new PopStateEvent('popstate'))
        } catch { /* ignore */ }
    }, [dispatch, sessionId])

    return (
        <div className={styles.section} data-active={isActiveSession}>
            <div
                className={styles.sectionHeader}
                onClick={() => setExpanded((p) => !p)}
            >
                <ChevronRight
                    className={`${styles.chevron}${expanded ? ' ' + styles.chevronExpanded : ''}`}
                />
                {editing ? (
                    <input
                        ref={inputRef}
                        className={styles.sectionTitleInput}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleEditSave}
                        onKeyDown={handleEditKeyDown}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <span className={styles.sectionTitle}>{displayTitle}</span>
                )}
                {!editing && (
                    <>
                        <button
                            className={styles.editButton}
                            onClick={handleEditStart}
                            aria-label={t('backstage.editSessionName')}
                        >
                            <Pencil className={styles.editIcon} />
                        </button>
                        <button
                            className={styles.editButton}
                            onClick={handleOpenSession}
                            aria-label={t('backstage.openSession')}
                            title={t('backstage.openSession')}
                        >
                            <SquareArrowOutUpRight className={styles.editIcon} />
                        </button>
                    </>
                )}
            </div>
            {expanded && (
                <div className={styles.grid}>
                    {nodes.map((node) => (
                        <SavedThumb
                            key={node.node_id}
                            nodeId={node.node_id}
                            sessionId={sessionId}
                            src={node.image_path}
                            isActiveSession={isActiveSession}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

interface SavedThumbProps {
    nodeId: string
    sessionId: string
    src: string
    isActiveSession: boolean
}

function SavedThumb({ nodeId, sessionId, src, isActiveSession }: SavedThumbProps): ReactElement {
    const dispatch = useAppDispatch()

    const handleClick = useCallback(() => {
        if (!isActiveSession) return
        dispatch(addCanvasItem({ sessionId, item: { id: nodeId, src } }))
        dispatch(toggleNode({ sessionId, nodeId }))
        window.dispatchEvent(
            new CustomEvent('canvas:focus-node', {
                detail: { nodeId, sessionId },
            })
        )
    }, [dispatch, nodeId, sessionId, src, isActiveSession])

    const handleDragStart = useCallback((e: DragEvent<HTMLImageElement>) => {
        const url = src || e.currentTarget.currentSrc || e.currentTarget.src
        if (!url) return
        setAiModelDragging(true)
        try {
            setAiModelDragData(e.dataTransfer, url)
        } catch {
            // Ignore drag errors
        }
    }, [src])

    const handleDragEnd = useCallback(() => {
        setAiModelDragging(false)
    }, [])

    return (
        <div
            className={styles.thumb}
            data-clickable={isActiveSession}
            onClick={handleClick}
        >
            <img
                className={styles.thumbImg}
                src={src}
                alt=""
                loading="lazy"
                draggable
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'
                }}
            />
        </div>
    )
}
